import React, { useState } from "react";
import { useParams, Redirect } from "react-router-dom";
import useAsyncEffect from 'use-async-effect';
import ApiService from '../../service/ApiService';
import UserKandis from '../../components/userKandis';
import SignInModal from '../../components/signInModal';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Theme, createStyles } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { apiGatewayUrlParam } from "../../config/ServiceConfig";
import { useAuth, useModal } from '../../hooks';

interface MyStyles {
    infoAlert: string;
    qrCode: string;
    notSignedInMessage: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
      infoAlert: {
          textAlign: 'left',
          width: 'fit-content',
          margin: 'auto'
      },
      qrCode: {
        textAlign: 'center',
      },
      notSignedInMessage: {
          padding: theme.spacing(2)
      }
  })
);

enum TagLookupState {
    NotSet,
    InProgress,
    KandiFound,
    KandiNotFound
}

const apiService = new ApiService(apiGatewayUrlParam);

const Tag: React.FunctionComponent = () => {
    let { uuid } = useParams() as any;
    const classes = useStyles() as MyStyles;

    // Use our new state management hooks
    const { isAuthenticated, isLoading, user } = useAuth();
    const signInModal = useModal('signIn');

    // Local state for tag-specific data
    const [tagLookupState, setTagLookupState] = useState(TagLookupState.NotSet);
    const [vanityHandle, setVanityHandle] = useState("");
    const [redirect, setRedirect] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(Date.now());

    useAsyncEffect(async isMounted => {
        setTagLookupState(TagLookupState.InProgress);
        console.log(`Checking if tag ${uuid} is associated with Kandi`);
        
        try {
            const data = await apiService.getRequest(`tags/${uuid}`);
            console.log("Success looking up tag " + JSON.stringify(data));
            
            if (!isMounted()) return;
            
            if (data.kandiId && data.vanityHandle) {
                setTagLookupState(TagLookupState.KandiFound);
                setVanityHandle(data.vanityHandle);
                setRedirect(true);
            } else {
                setTagLookupState(TagLookupState.KandiNotFound);
            }
        } catch (error) {
            console.error("Error looking up tag:", error);
            if (!isMounted()) return;
            setTagLookupState(TagLookupState.KandiNotFound);
        }
    }, [uuid, lastUpdated]);

    const signedInExperience = function () {
        if (!user) return null;
        
        return (
            <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
            >
                <Grid item>
                    <Tooltip title="Attach QR code sticker to your kandi, then select the corresponding kandi from your collection below to enable tracking it over trades.">
                        <Alert className={classes.infoAlert} severity="info">
                            <AlertTitle>
                                QR code not registered.
                            </AlertTitle>
                            Click on 'Add QR' next to the Kandi Below to register this qr code to it.
                        </Alert>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <UserKandis 
                        userName={user.username || "Anonymous"}
                        lastUpdated={Date.now()}
                        tag={uuid}
                        onSetTag={() => { setLastUpdated(Date.now()) }}
                    />
                </Grid>
            </Grid>
        );
    }

    const signedOutExperience = function() {
        return (
            <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
            >
                <Grid item className={classes.notSignedInMessage}>
                    <Typography variant="body1">
                        QR code not registered. Sign in / sign up to register the qr code.
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        color="primary"
                        onClick={signInModal.open}
                        variant="contained"
                    >
                        Sign in / Sign up
                    </Button>
                </Grid>
            </Grid>
        );
    }

    const renderQRActions = function() {
        if (isLoading) {
            return (
                <Grid container justifyContent="center" style={{ padding: '2rem' }}>
                    <CircularProgress />
                </Grid>
            );
        }
        
        if (isAuthenticated && user) {
            return signedInExperience();
        } else {
            return signedOutExperience();
        }
    }

    // Show loading state while checking tag
    if (tagLookupState === TagLookupState.InProgress) {
        return (
            <Grid container justifyContent="center" style={{ marginTop: '2rem' }}>
                <CircularProgress />
            </Grid>
        );
    }

    // Redirect to kandi page if tag is associated with a kandi
    if (redirect) {
        return <Redirect to={{
            pathname: `/k/${vanityHandle}`,
            state: { tagId: uuid }
        }} />
    }

    // Show QR registration interface if tag is not associated with any kandi
    if (tagLookupState === TagLookupState.KandiNotFound) {
        return (
            <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={4}
            >
                <Grid
                    item
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Grid item>
                        <Typography variant="body1">
                            QR Code ID
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="body1">
                            <strong>{uuid}</strong>
                        </Typography>
                    </Grid>
                </Grid>
                <SignInModal 
                    onClose={signInModal.close}
                    open={signInModal.isOpen}
                />
                <Grid item>
                    {renderQRActions()}
                </Grid>
            </Grid>
        )
    }

    // Default case - should not reach here normally
    return null;
}

export default Tag;
